import type { ContentGraph } from './graph';

/**
 * Who a result is written as. Decided 2026-10-05 (platform
 * docs/architecture/multi-region/core-data.md 7e): as a member of the Stronger Memory group,
 * Core's `GROUP_MEMBER`, so the group owns its members' results, they are stored at the group's
 * home and they move with it. Without a group to write in (none in this environment, or the
 * person isn't in it), as the person alone, as echo always did.
 *
 * Core grants a `GROUP_MEMBER` write to that member in that group, not to the group's leaders
 * or other members, who see the shared summary only. Written as a member, each exercise is also
 * shared with the group itself, its admins (`shareResultsWithGroup`).
 */
export type WriteScope = { kind: 'person' } | { kind: 'member'; groupActorUuid: string };

export const AS_PERSON: WriteScope = { kind: 'person' };

export function asMemberOf(groupActorUuid: string): WriteScope {
  return { kind: 'member', groupActorUuid };
}

/** Core's `performAs` for a write in `scope`; none for the person alone, Core's default. */
export function performAsFor(
  scope: WriteScope,
): { principalType: 'GROUP_MEMBER'; actorUuid: string } | undefined {
  return scope.kind === 'member'
    ? { principalType: 'GROUP_MEMBER', actorUuid: scope.groupActorUuid }
    : undefined;
}

/** One string per scope, for keying rows by day and scope. */
export function scopeKey(scope: WriteScope): string {
  return scope.kind === 'member' ? `member:${scope.groupActorUuid}` : 'person';
}

export function sameScope(a: WriteScope | undefined, b: WriteScope): boolean {
  return a !== undefined && scopeKey(a) === scopeKey(b);
}

/**
 * The scope a node was written in, from its `AUTHORED_BY` edge. Undefined when Core sent none,
 * or an author echo never writes as: nothing may then be added to the node, because Core lets
 * only its exact author (principal and scope) add to it.
 */
export function writtenScopeOf(graph: ContentGraph, nodeUuid: string): WriteScope | undefined {
  const authored = graph.actorEdges.find(
    (edge) => edge.contentUuid === nodeUuid && edge.edgeType === 'AUTHORED_BY',
  );
  if (authored?.audience.principalType === 'USER') return AS_PERSON;
  if (authored?.audience.principalType === 'GROUP_MEMBER' && authored.audience.scopeUuid) {
    return asMemberOf(authored.audience.scopeUuid);
  }
  return undefined;
}

/** A scope read back from storage (the outbox); anything else is no scope. */
export function parseScope(value: unknown): WriteScope | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const scope = value as { kind?: unknown; groupActorUuid?: unknown };
  if (scope.kind === 'person') return AS_PERSON;
  if (scope.kind === 'member' && typeof scope.groupActorUuid === 'string' && scope.groupActorUuid) {
    return asMemberOf(scope.groupActorUuid);
  }
  return undefined;
}
